import { colors } from "@material-ui/core";
import { ColDef, ValueFormatterParams } from "@material-ui/data-grid";
import { setResponse } from "features/admin/adminSlice";
import OrderProducts from "features/admin/Orders/components/OrderProducts";
import {
  deleteOrder,
  fetchOrderedElements,
  fetchOrders,
  selectAdminOrders,
  selectAdminOrdersIsLoading,
  setOrderAgreed,
  setOrderCompleted,
  setOrderElements,
} from "features/admin/Orders/ordersAdminSlice";
import { ObjectType } from "features/types";
import React, { useEffect, useState } from "react";
import { AiFillCloseCircle } from "react-icons/ai";
import { MdCheckCircle } from "react-icons/md";
import { batch, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components/macro";
import GridTable from "../Common/GridTable";
import { CenteredTableRow } from "./columns/UpdatableCountField";
import DualPrice from "components/Price";

interface OrdersTableProps {}

const OrdersTable: React.FC<OrdersTableProps> = ({}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selected, setSelected] = useState<any>(null);

  const columns: ColDef[] = [
    {
      field: "order_date",
      headerName: "Дата",
      width: 100,
      renderCell: (params: ValueFormatterParams) => {
        const date = new Date(params.value as string);
        const stringDate = date.toLocaleDateString();
        return <div>{stringDate}</div>;
      },
    },
    {
      field: "orderId",
      headerName: "Поръчка номер",
      width: 150,
    },
    {
      field: "firstName",
      headerName: "Име",
      width: 200,
      // valueGetter: getFullName,
      // sortComparator: (v1, v2, cellParams1, cellParams2) =>
      //   getFullName(cellParams1).localeCompare(getFullName(cellParams2)),
    },
    {
      field: "address",
      headerName: "Адрес",
      width: 400,
    },
    {
      field: "paymentType",
      headerName: "Начин на плащане",
      width: 175,
    },
    {
      field: "total_price",
      headerName: "Обща цена",
      width: 125,
      renderCell: (params: any) => (
        <strong>
          <DualPrice
            bgn={params.value}
            eur={params.row.total_price_eur}
            showBgn
          />
        </strong>
      ),
    },
    {
      field: "isAgreed",
      headerName: "Одобрена",
      width: 100,
      renderCell: (params: ValueFormatterParams) => (
        <CenteredTableRow>
          {params.value ? (
            <MdCheckCircle
              color={colors.green[500]}
              style={{ width: 32, height: 32 }}
            />
          ) : (
            <AiFillCloseCircle
              color={colors.red[500]}
              style={{ width: 32, height: 32 }}
            />
          )}
        </CenteredTableRow>
      ),
      align: "center",
    },
    {
      field: "order_completed",
      headerName: "Изпълнена",
      width: 150,
      renderCell: (params: ValueFormatterParams) => (
        <CenteredTableRow>
          {params.value ? (
            <MdCheckCircle
              color={colors.green[500]}
              style={{ width: 32, height: 32 }}
            />
          ) : (
            <AiFillCloseCircle
              color={colors.red[500]}
              style={{ width: 32, height: 32 }}
            />
          )}
        </CenteredTableRow>
      ),
    },
    {
      field: "id",
      headerName: "Одобряване",
      width: 200,
      renderCell: (params: ValueFormatterParams) => (
        <CenteredTableRow>
          <Button
            onClick={() => agreeOrder(params.row.id as number)}
            disabled={params.row.isAgreed}
          >
            Одобрена
          </Button>
          <Button
            onClick={() => completeOrder(params.row.id as number)}
            disabled={!params.row.isAgreed || params.row.order_completed}
          >
            Изпълнена
          </Button>
        </CenteredTableRow>
      ),
    },
  ];

  const orders = useSelector(selectAdminOrders);
  const isLoading = useSelector(selectAdminOrdersIsLoading);

  useEffect(() => {
    dispatch(fetchOrders());

    return () => {
      dispatch(setOrderElements([]));
    };
  }, []);

  useEffect(() => {
    if (selected) {
      dispatch(fetchOrderedElements(selected.items));
    }
  }, [selected]);

  const add = () => {
    navigate(`/orders/add`);
  };
  const refresh = () => {
    dispatch(fetchOrders());
  };
  const remove = (id: string[]) => {
    const allowDelete = window.confirm("Искате ли да изтриете поръчката?");

    if (allowDelete) {
      const _refId = orders.find((item) => item.id === parseInt(id[0]))?.refId;

      _refId &&
        batch(() => {
          dispatch(setResponse({ message: "", hasError: false }));
          dispatch(deleteOrder(_refId));
        });
    }
  };
  const edit = (selectedId: string) => {
    const selectedItemRefId = orders.find(
      (item: { id: number }) => item.id === parseInt(selectedId)
    )?.refId;
    navigate(`/orders/edit/${selectedItemRefId}`);
  };

  const selectedCallback = (idx: number) => {
    setSelected(orders[idx]);
  };

  const completeOrder = (rowIdx: number) => {
    const order = orders[rowIdx - 1];
    dispatch(setOrderCompleted(order));
  };

  const agreeOrder = (rowIdx: number) => {
    const order = orders[rowIdx - 1];
    dispatch(setOrderAgreed(order));
  };

  return (
    <OrdersContainer>
      <GridTable
        autoPageSize={true}
        add={add}
        refresh={refresh}
        remove={remove}
        edit={edit}
        data={orders}
        columns={columns}
        isLoading={isLoading}
        searchProperty={"firstName"}
        selectedRowCallback={selectedCallback}
        checkboxes={false}
        height={450}
      />
      <OrderProducts
        noProductsMessage="Не е селектирана поръчка"
        objectTypes={Object.keys(ObjectType)}
      />
    </OrdersContainer>
  );
};

const OrdersContainer = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

export const Button = styled.button`
  color: white;
  padding: 5px 10px;
  text-align: center;
  background-color: ${colors.green[500]};
  border-radius: 5px;
  height: 80%;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;

  &:hover {
    background-color: ${colors.green[700]};
  }

  &:disabled {
    background-color: ${colors.grey[500]};
    color: ${colors.grey[300]};
  }

  & + & {
    margin-left: 12px;
  }
`;
export default OrdersTable;
